import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { getListWilayah } from "@/services/getData";
import { Wilayah } from "@/types";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MdSync } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const getData = async ({
  id_feeder,
  jenis,
}: {
  id_feeder?: string | null;
  jenis?: string | null;
}) => {
  const response = await api.get(`/master/sekolah/sync`, {
    params: {
      id_feeder,
      jenis,
    },
  });
  return response.data;
};

export default function FormSync({
  closeModal,
  isSyncAll = false,
}: {
  closeModal: () => void;
  isSyncAll?: boolean;
}) {
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [valueProvinsi, setValueProvinsi] = useState("");
  const [valueKabupaten, setValueKabupaten] = useState("");
  const [valueKecamatan, setValueKecamatan] = useState("");
  const [valueJenis, setValueJenis] = useState("");

  const { data: dataProvinsi } = useQuery({
    queryKey: ["listProvinsi"],
    queryFn: () => getListWilayah(undefined, "1"),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: wilayah.id_feeder,
      })),
  });

  const { data: dataKabupaten, refetch: refetchKabupaten } = useQuery({
    queryKey: ["listKabupaten", valueProvinsi],
    queryFn: () => getListWilayah(undefined, undefined, valueProvinsi),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: wilayah.id_feeder,
      })),
    enabled: !!valueProvinsi,
  });

  const { data: dataKecamatan, refetch: refetchKecamatan } = useQuery({
    queryKey: ["listKecamatan", valueKabupaten],
    queryFn: () => getListWilayah(undefined, undefined, valueKabupaten),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: wilayah.id_feeder,
      })),
    enabled: !!valueKecamatan,
  });

  useEffect(() => {
    if (valueProvinsi) refetchKabupaten();
  }, [valueProvinsi, refetchKabupaten]);

  useEffect(() => {
    if (valueKabupaten) refetchKecamatan();
  }, [valueKabupaten, refetchKecamatan]);

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: getData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSekolah"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSyncSekolah = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isSyncAll && !valueKecamatan) {
      toast.error("Pilih hingga level kecamatan");
      return;
    }

    isSyncAll
      ? mutate({ id_feeder: null })
      : mutate({ id_feeder: valueKecamatan, jenis: valueJenis });
  };

  return (
    <form onSubmit={handleSyncSekolah} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<MdSync />}
        title="Sinkronisasi Data Sekolah"
        description={`Apakah anda yakin akan melakukan sinkronisasi data sekolah?`}
      />
      {!isSyncAll && (
        <>
          <div>
            <Label>Provinsi</Label>
            <Combobox
              open={openProvinsi}
              setOpen={setOpenProvinsi}
              value={valueProvinsi}
              setValue={setValueProvinsi}
              options={dataProvinsi}
              placeholder="Pilih provinsi"
            />
          </div>
          <div>
            <Label>Kabupaten</Label>
            <Combobox
              open={openKabupaten}
              setOpen={setOpenKabupaten}
              value={valueKabupaten}
              setValue={setValueKabupaten}
              options={dataKabupaten}
              placeholder="Pilih kabupaten"
            />
          </div>
          <div>
            <Label>Kecamatan</Label>
            <Combobox
              open={openKecamatan}
              setOpen={setOpenKecamatan}
              value={valueKecamatan}
              setValue={setValueKecamatan}
              options={dataKecamatan}
              placeholder="Pilih kecamatan"
            />
          </div>
          <div>
            <Label>Jenis</Label>
            <Select
              required
              value={valueJenis}
              onValueChange={(value) => {
                setValueJenis(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Jenis"} />
              </SelectTrigger>
              <SelectContent>
                {["dikmen", "dikmas"]?.map((value: string) => (
                  <SelectItem key={value} value={`${value}`}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          disabled={isPending}
          onClick={() => closeModal()}
        >
          Batal
        </Button>
        <Button
          variant={"default"}
          className="flex items-center gap-3"
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Sinkron"
          )}
        </Button>
      </div>
    </form>
  );
}
